import React from 'react';
import { Link } from 'react-router-dom';
import useSeo from '../hooks/useSeo';
import { SiteHeader } from './ServicesIndex';
import SiteFooter from '../components/SiteFooter';

/**
 * Reusable page wrapper for static legal/info pages.
 */
export function LegalPageLayout({ title, subtitle, lastUpdated, children, onOpenCalculator, breadcrumbLabel }) {
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
      <SiteHeader onOpenCalculator={onOpenCalculator} />

      <div className="bg-slate-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-4 text-sm text-gray-500" data-testid="breadcrumb">
          <Link to="/" className="hover:text-brand-green">Home</Link>
          <span className="mx-2">›</span>
          <span className="text-gray-700 font-medium">{breadcrumbLabel || title}</span>
        </div>
      </div>

      <section className="bg-gradient-to-br from-brand-blue to-brand-blue-light text-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 py-12 md:py-16">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>{title}</h1>
          {subtitle && <p className="mt-3 text-base sm:text-lg text-white/80 max-w-2xl">{subtitle}</p>}
          {lastUpdated && <p className="mt-4 text-xs text-white/55 uppercase tracking-wider">Last updated: {lastUpdated}</p>}
        </div>
      </section>

      <main className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 py-10 sm:py-14">
        <article className="prose prose-slate max-w-none prose-headings:text-brand-blue prose-headings:font-semibold prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3 prose-h3:text-base prose-h3:mt-5 prose-h3:mb-2 prose-p:text-[15px] prose-p:leading-relaxed prose-p:text-gray-700 prose-li:text-[15px] prose-li:text-gray-700 prose-li:leading-relaxed prose-strong:text-brand-blue prose-a:text-brand-green prose-a:no-underline hover:prose-a:underline">
          {children}
        </article>
      </main>

      <SiteFooter />
    </div>
  );
}

/* ============== PRIVACY POLICY ============== */
export function PrivacyPolicy({ onOpenCalculator }) {
  useSeo('Privacy Policy', 'How EntoRid collects, uses and protects your personal data when you use our pest control services.');
  return (
    <LegalPageLayout
      title="Privacy Policy"
      subtitle="Your privacy matters. Here's exactly what data we collect and how we use it."
      lastUpdated="February 2026"
      onOpenCalculator={onOpenCalculator}
    >
      <p>
        At <strong>EntoRid</strong>, we respect your privacy and are committed to protecting your personal information.
        This Privacy Policy explains what data we collect, why we collect it, and how we keep it secure.
      </p>

      <h2>1. Information We Collect</h2>
      <p>
        We collect basic customer data necessary to deliver our pest control services:
      </p>
      <ul>
        <li><strong>Name</strong> — to identify you as our customer</li>
        <li><strong>Phone number</strong> — to schedule appointments and provide support</li>
        <li><strong>Email address</strong> (optional) — to send booking confirmations and receipts</li>
        <li><strong>City &amp; service address</strong> — to dispatch our certified technicians</li>
        <li><strong>Service preferences</strong> — to recommend the right treatment</li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <p>
        Your data is used <strong>only</strong> for the following purposes:
      </p>
      <ul>
        <li>Scheduling and delivering pest control services</li>
        <li>Sending booking confirmations, receipts, and follow-up reminders</li>
        <li>Responding to enquiries through phone, WhatsApp, or email</li>
        <li>Improving our services based on customer feedback</li>
        <li>Issuing service warranties and free re-treatments under our plans</li>
      </ul>

      <h2>3. Data Sharing</h2>
      <p>
        <strong>We do not sell, rent or trade your data to third parties.</strong> Your information is never disclosed to advertisers or external marketing companies.
      </p>
      <p>
        We share data only with:
      </p>
      <ul>
        <li>Our certified technicians who will visit your premises</li>
        <li>Payment processors (Razorpay) for secure transaction processing</li>
        <li>Email service providers (Resend) solely to deliver booking confirmations</li>
        <li>Government authorities when required by law</li>
      </ul>

      <h2>4. Data Security</h2>
      <p>
        We implement industry-standard security measures to protect your information:
      </p>
      <ul>
        <li>HTTPS encryption on every page of our website</li>
        <li>Secure database storage with restricted admin access</li>
        <li>Payment data is never stored on our servers — handled entirely by Razorpay</li>
        <li>Regular security audits and access reviews</li>
      </ul>

      <h2>5. Your Rights</h2>
      <p>You have the right to:</p>
      <ul>
        <li>Request a copy of the personal data we hold about you</li>
        <li>Ask us to correct inaccurate information</li>
        <li>Request deletion of your data (subject to legal record-keeping requirements)</li>
        <li>Opt out of marketing communications at any time</li>
      </ul>

      <h2>6. Cookies</h2>
      <p>
        Our website uses minimal essential cookies to remember your session and improve your experience. We do not use tracking cookies for advertising purposes.
      </p>

      <h2>7. Contact Us</h2>
      <p>
        For any privacy-related questions or to exercise your rights, contact us at:
      </p>
      <ul>
        <li>📧 Email: <a href="mailto:official.entorid@gmail.com">official.entorid@gmail.com</a></li>
        <li>📞 Phone: <a href="tel:+917819988010">+91 78199 88010</a></li>
        <li>📍 Address: Weblan Building, IT Park, 45 B, Sahastradhara Road, Doon IT Park, Govind Vihar, Dehradun, Uttarakhand – 248013</li>
      </ul>
    </LegalPageLayout>
  );
}

/* ============== TERMS & CONDITIONS ============== */
export function TermsConditions({ onOpenCalculator }) {
  useSeo('Terms and Conditions', 'EntoRid terms and conditions covering service availability, pricing, customer responsibilities, and liability.');
  return (
    <LegalPageLayout
      title="Terms & Conditions"
      subtitle="The rules and expectations that govern our pest control services."
      lastUpdated="February 2026"
      onOpenCalculator={onOpenCalculator}
      breadcrumbLabel="Terms & Conditions"
    >
      <p>
        By booking services with <strong>EntoRid</strong>, you agree to the following terms and conditions.
        Please read them carefully before placing your order.
      </p>

      <h2>1. Service Availability</h2>
      <p>
        Our pest control services are subject to availability based on technician schedules, geographical coverage, and seasonal demand.
        While we strive to provide same-day or next-day service, we cannot guarantee specific time slots without prior confirmation.
      </p>

      <h2>2. Pricing</h2>
      <p>
        All prices listed on our website and calculator are <strong>indicative starting prices</strong>. Final pricing may vary based on:
      </p>
      <ul>
        <li>Severity of infestation discovered during inspection</li>
        <li>Property size, layout, and accessibility</li>
        <li>Type and quantity of treatment chemicals required</li>
        <li>Multiple pest types requiring different treatments</li>
        <li>Special equipment or follow-up visits required</li>
      </ul>
      <p>
        Our technician will provide a <strong>final written quote</strong> before commencing any work. You may decline at this stage with no obligation.
      </p>

      <h2>3. Customer Responsibilities</h2>
      <p>
        For successful service delivery, you agree to:
      </p>
      <ul>
        <li>Provide <strong>accurate contact details</strong>, address, and access information</li>
        <li>Be present (or appoint a responsible adult) during the service</li>
        <li>Disclose any allergies, pets, or special concerns to our technician beforehand</li>
        <li>Allow our team safe access to all infested areas</li>
        <li>Follow post-treatment instructions (e.g., 2-hour airing, food storage advice)</li>
      </ul>

      <h2>4. Service Delivery &amp; Warranty</h2>
      <p>
        Each service comes with a written warranty (1-year, 3-year or 5-year depending on the plan).
        During the warranty period, if pests return, we will re-treat free of charge — provided you have followed our prevention guidelines.
      </p>
      <h3>Conditions for warranty:</h3>
      <ul>
        <li>Customer notifies us within 30 days of pest re-appearance</li>
        <li>No structural changes have been made to treated areas</li>
        <li>No third-party pest control work has been done in between</li>
      </ul>

      <h2>5. Limitation of Liability</h2>
      <p>
        EntoRid is <strong>not liable</strong> for:
      </p>
      <ul>
        <li>Pre-existing structural damage caused by pests prior to our service</li>
        <li>Damage to fragile items the customer failed to disclose or move</li>
        <li>Health issues arising from pre-existing allergies or sensitivities not disclosed before service</li>
        <li>Indirect, consequential, or incidental damages</li>
      </ul>
      <p>
        Our maximum liability under any service is limited to the total amount paid for that service.
      </p>

      <h2>6. Cancellation</h2>
      <p>
        You may cancel your booking up to <strong>4 hours before</strong> the scheduled service time without any charges.
        Cancellations within 4 hours of the scheduled time may incur a small visit fee to compensate the technician.
      </p>

      <h2>7. Force Majeure</h2>
      <p>
        EntoRid shall not be liable for service delays or failures arising from natural disasters, government restrictions, strikes, severe weather, or any event beyond our reasonable control.
      </p>

      <h2>8. Governing Law</h2>
      <p>
        These terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Dehradun, Uttarakhand.
      </p>

      <h2>9. Contact</h2>
      <p>
        For any questions about these terms, contact us at <a href="mailto:official.entorid@gmail.com">official.entorid@gmail.com</a> or call <a href="tel:+917819988010">+91 78199 88010</a>.
      </p>
    </LegalPageLayout>
  );
}

/* ============== REFUND POLICY ============== */
export function RefundPolicy({ onOpenCalculator }) {
  useSeo('Refund Policy', 'EntoRid refund policy — when refunds apply, how partial refunds work, and how to request a refund.');
  return (
    <LegalPageLayout
      title="Refund Policy"
      subtitle="Clear, fair refund terms for our pest control services."
      lastUpdated="February 2026"
      onOpenCalculator={onOpenCalculator}
    >
      <p>
        At <strong>EntoRid</strong>, customer satisfaction is at the heart of everything we do.
        This Refund Policy explains when refunds are available, how to claim one, and what to expect.
      </p>

      <h2>1. Full Refunds</h2>
      <p>
        You are entitled to a <strong>full refund</strong> in the following situations:
      </p>
      <ul>
        <li>Our team is unable to deliver the booked service due to scheduling conflicts on our end</li>
        <li>You cancel your booking at least 4 hours before the scheduled service time</li>
        <li>Payment was charged twice due to a technical error</li>
        <li>You are charged for services that were never performed</li>
      </ul>

      <h2>2. No Refund After Service Completion</h2>
      <p>
        Once a pest control service has been <strong>fully delivered</strong> by our technician, no refunds are issued.
        However, you remain protected under our service warranty — if pests return during the warranty period, we will provide free re-treatment.
      </p>

      <h2>3. Partial Refunds (Case-by-Case)</h2>
      <p>
        Partial refunds may be issued at our discretion in the following situations:
      </p>
      <ul>
        <li>The treatment was substantially less effective than promised, even after free re-treatment attempts</li>
        <li>Service quality fell significantly below our standards (e.g., technician didn't complete agreed scope)</li>
        <li>You are dissatisfied for documented reasons within 7 days of service</li>
      </ul>
      <p>
        The amount refunded depends on the situation and is determined after fair review by our team.
      </p>

      <h2>4. Refund Process</h2>
      <p>
        To request a refund, please:
      </p>
      <ol>
        <li>Contact us at <a href="mailto:official.entorid@gmail.com">official.entorid@gmail.com</a> or call <a href="tel:+917819988010">+91 78199 88010</a> within 7 days of the service</li>
        <li>Provide your booking reference, payment ID, and reason for the refund</li>
        <li>Our team will review your request within <strong>2 business days</strong></li>
        <li>Approved refunds are processed back to the original payment method within <strong>5–7 business days</strong></li>
      </ol>

      <h2>5. AMC (Annual Maintenance Contracts)</h2>
      <p>
        For AMC plans, you may cancel within the first 30 days for a full refund.
        After 30 days, partial refunds are calculated as follows:
      </p>
      <ul>
        <li>Pro-rated based on number of visits already used</li>
        <li>A small processing fee may apply (typically ₹500 or 5% of the AMC value, whichever is lower)</li>
      </ul>

      <h2>6. Non-Refundable Items</h2>
      <p>
        The following are non-refundable:
      </p>
      <ul>
        <li>Inspection or call-out fees once the technician has visited the property</li>
        <li>Services that were properly delivered as agreed</li>
        <li>Custom chemical orders for specialised treatments</li>
      </ul>

      <h2>7. Disputes</h2>
      <p>
        If you disagree with our refund decision, you may escalate the matter via written request to our head office within 14 days.
        We are committed to fair resolution and customer satisfaction.
      </p>

      <h2>8. Contact Us</h2>
      <p>
        Need to discuss a refund? Reach out anytime:
      </p>
      <ul>
        <li>📧 <a href="mailto:official.entorid@gmail.com">official.entorid@gmail.com</a></li>
        <li>📞 <a href="tel:+917819988010">+91 78199 88010</a></li>
        <li>💬 <a href="https://api.whatsapp.com/send?phone=919410110910" target="_blank" rel="noreferrer">WhatsApp +91 94101 10910</a></li>
      </ul>
    </LegalPageLayout>
  );
}
